<template>
  <div id="providerModal" ref="modalEl" class="modal fade" tabindex="-1" aria-labelledby="providerModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
      <form class="modal-content" novalidate @submit.prevent="save">
        <div class="modal-header">
          <h2 id="providerModalLabel" class="modal-title">{{ title }}</h2>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
        </div>

        <div class="modal-body">
          <div v-if="form.user_id" class="dp-driver" role="note">
            <i class="ph ph-moped" aria-hidden="true"></i>
            <div>
              <strong>Livreur prestataire : {{ driverName || form.name }}</strong>
              <span>
                Il reste un livreur (compte, frais, villes). À l'expédition d'un bon d'envoi vers ce prestataire, les colis sont envoyés
                à son API puis un bon de distribution est créé automatiquement à son nom.
              </span>
            </div>
          </div>

          <!-- General -->
          <fieldset class="dp-section">
            <legend>Général</legend>
            <div class="row g-3">
              <div class="col-md-5 sx-field">
                <label for="dp-name">Nom <span class="sx-req" aria-hidden="true">*</span></label>
                <input id="dp-name" v-model="form.name" type="text" class="form-control" :class="{ 'is-invalid': errors.name }" maxlength="100" placeholder="Ex. Sendit" />
                <span v-if="errors.name" class="sx-err">{{ errors.name }}</span>
              </div>
              <div class="col-md-4 sx-field">
                <label for="dp-code">Code</label>
                <input id="dp-code" v-model="form.code" type="text" class="form-control" maxlength="50" placeholder="Ex. SND" />
              </div>
              <div class="col-md-3 sx-field">
                <label for="dp-active">Statut</label>
                <div class="form-check form-switch dp-switch">
                  <input id="dp-active" v-model="form.active" class="form-check-input" type="checkbox" role="switch" />
                  <label class="form-check-label" for="dp-active">{{ form.active ? "Actif" : "Inactif" }}</label>
                </div>
              </div>
            </div>
          </fieldset>

          <!-- Connection -->
          <fieldset class="dp-section">
            <legend>Connexion</legend>
            <p class="sx-help dp-auth-help">
              Facultatif à la création. Tant que l'URL et l'endpoint ne sont pas renseignés, le prestataire n'est pas proposé à l'expédition.
            </p>
            <div class="row g-3">
              <div class="col-md-6 sx-field">
                <label for="dp-url">URL de l'API</label>
                <input id="dp-url" v-model="form.api_base_url" type="url" class="form-control" :class="{ 'is-invalid': errors.api_base_url }" placeholder="https://api.prestataire.ma/v1" />
                <span v-if="errors.api_base_url" class="sx-err">{{ errors.api_base_url }}</span>
              </div>
              <div class="col-md-6 sx-field">
                <label for="dp-endpoint">Endpoint d'ajout de colis</label>
                <input id="dp-endpoint" v-model="form.api_create_endpoint" type="text" class="form-control" :class="{ 'is-invalid': errors.api_create_endpoint }" placeholder="/parcels" />
                <span v-if="errors.api_create_endpoint" class="sx-err">{{ errors.api_create_endpoint }}</span>
                <span v-else class="sx-help" v-text="'Relatif à l\'URL de l\'API, ou URL complète. Variables possibles : {{token}}, {{username}}, {{extra.cle}} (ex. /customers/{{extra.client_id}}/{{token}}/add-parcel).'"></span>
              </div>
              <div class="col-md-4 sx-field">
                <label for="dp-method">Méthode</label>
                <select id="dp-method" v-model="form.api_method" class="form-select">
                  <option>GET</option>
                  <option>POST</option>
                  <option>PUT</option>
                  <option>PATCH</option>
                </select>
              </div>
              <div class="col-md-4 sx-field">
                <label for="dp-format">Format du corps</label>
                <select id="dp-format" v-model="form.api_body_format" class="form-select">
                  <option value="json">JSON</option>
                  <option value="form">Formulaire (x-www-form-urlencoded)</option>
                </select>
              </div>
              <div class="col-md-4 sx-field">
                <label for="dp-timeout">Délai d'attente (s)</label>
                <input id="dp-timeout" v-model.number="form.api_timeout" type="number" min="5" max="120" class="form-control" :class="{ 'is-invalid': errors.api_timeout }" />
                <span v-if="errors.api_timeout" class="sx-err">{{ errors.api_timeout }}</span>
              </div>
            </div>
          </fieldset>

          <!-- Authentication -->
          <fieldset class="dp-section">
            <legend>Authentification</legend>
            <div class="sx-seg dp-seg" role="radiogroup" aria-label="Type d'authentification">
              <button
                v-for="opt in authOptions"
                :key="opt.value"
                type="button"
                role="radio"
                :aria-checked="form.api_auth_type === opt.value"
                :class="{ 'is-active': form.api_auth_type === opt.value }"
                @click="form.api_auth_type = opt.value"
              >
                {{ opt.label }}
              </button>
            </div>
            <p class="sx-help dp-auth-help">{{ authOf(form.api_auth_type)?.help }}</p>

            <div class="row g-3">
              <div v-if="['header', 'query'].includes(form.api_auth_type)" class="col-md-6 sx-field">
                <label for="dp-keyname">{{ form.api_auth_type === "header" ? "Nom de l'en-tête" : "Nom du paramètre" }} <span class="sx-req" aria-hidden="true">*</span></label>
                <input id="dp-keyname" v-model="form.api_key_name" type="text" class="form-control" :class="{ 'is-invalid': errors.api_key_name }" :placeholder="form.api_auth_type === 'header' ? 'X-API-KEY' : 'api_key'" />
                <span v-if="errors.api_key_name" class="sx-err">{{ errors.api_key_name }}</span>
              </div>
              <div v-if="['bearer', 'header', 'query', 'login', 'url'].includes(form.api_auth_type)" class="col-md-6 sx-field">
                <label for="dp-token">Clé / token<template v-if="form.api_auth_type === 'login'"> (facultatif)</template></label>
                <input id="dp-token" v-model="form.api_token" type="password" class="form-control" autocomplete="new-password" :placeholder="form.api_token_hint || 'Coller la clé'" />
                <span class="sx-help">
                  <template v-if="form.api_token_set">Enregistré ({{ form.api_token_hint }}). Laisser vide pour le conserver. <a href="#" @click.prevent="form.clear_token = !form.clear_token">{{ form.clear_token ? "Annuler la suppression" : "Supprimer" }}</a></template>
                  <template v-else>Stocké chiffré, jamais réaffiché.</template>
                </span>
              </div>
              <div v-if="['basic', 'login'].includes(form.api_auth_type)" class="col-md-6 sx-field">
                <label for="dp-user">Identifiant <span v-if="form.api_auth_type === 'basic'" class="sx-req" aria-hidden="true">*</span></label>
                <input id="dp-user" v-model="form.api_username" type="text" class="form-control" :class="{ 'is-invalid': errors.api_username }" autocomplete="off" />
                <span v-if="errors.api_username" class="sx-err">{{ errors.api_username }}</span>
              </div>
              <div v-if="['basic', 'login'].includes(form.api_auth_type)" class="col-md-6 sx-field">
                <label for="dp-pass">Mot de passe / secret</label>
                <input id="dp-pass" v-model="form.api_password" type="password" class="form-control" autocomplete="new-password" :placeholder="form.api_password_set ? '••••••••' : ''" />
                <span class="sx-help">
                  <template v-if="form.api_password_set">Enregistré. Laisser vide pour le conserver. <a href="#" @click.prevent="form.clear_password = !form.clear_password">{{ form.clear_password ? "Annuler la suppression" : "Supprimer" }}</a></template>
                  <template v-else>Stocké chiffré, jamais réaffiché.</template>
                </span>
              </div>
              <template v-if="form.api_auth_type === 'login'">
                <div class="col-md-6 sx-field">
                  <label for="dp-authep">Endpoint d'authentification <span class="sx-req" aria-hidden="true">*</span></label>
                  <input id="dp-authep" v-model="form.api_auth_endpoint" type="text" class="form-control" :class="{ 'is-invalid': errors.api_auth_endpoint }" placeholder="/login" />
                  <span v-if="errors.api_auth_endpoint" class="sx-err">{{ errors.api_auth_endpoint }}</span>
                </div>
                <div class="col-md-6 sx-field">
                  <label for="dp-authpath">Chemin du token dans la réponse</label>
                  <input id="dp-authpath" v-model="form.api_auth_token_path" type="text" class="form-control" placeholder="data.token" />
                </div>
                <div class="col-12 sx-field">
                  <label for="dp-authbody">Corps de la requête d'authentification (JSON)</label>
                  <textarea id="dp-authbody" v-model="form.api_auth_payload" class="form-control dp-code" :class="{ 'is-invalid': errors.api_auth_payload }" rows="3" spellcheck="false" placeholder='{"public_key": "{{username}}", "secret_key": "{{password}}"}'></textarea>
                  <span v-if="errors.api_auth_payload" class="sx-err">{{ errors.api_auth_payload }}</span>
                  <span v-else class="sx-help">Variables : <code v-text="'{{username}}'"></code>, <code v-text="'{{password}}'"></code>, <code v-text="'{{token}}'"></code>, <code v-text="'{{extra.*}}'"></code>. Le token obtenu est envoyé en Bearer et gardé 50 min.</span>
                </div>
              </template>
            </div>
          </fieldset>

          <!-- Payload -->
          <fieldset class="dp-section">
            <legend>Données envoyées pour chaque colis</legend>
            <div class="row g-3">
              <div class="col-lg-8 sx-field">
                <label for="dp-payload">Modèle JSON <span class="sx-req" aria-hidden="true">*</span></label>
                <textarea
                  id="dp-payload"
                  ref="payloadEl"
                  v-model="form.api_payload_template"
                  class="form-control dp-code"
                  :class="{ 'is-invalid': errors.api_payload_template || payloadJsonError }"
                  rows="14"
                  spellcheck="false"
                ></textarea>
                <span v-if="errors.api_payload_template" class="sx-err">{{ errors.api_payload_template }}</span>
                <span v-else-if="payloadJsonError" class="sx-err">{{ payloadJsonError }}</span>
                <span v-else class="sx-help">
                  Une valeur égale à une seule variable garde son type (nombre, booléen) ; sinon les variables sont insérées dans le texte.
                </span>
              </div>
              <div class="col-lg-4">
                <div class="dp-vars">
                  <strong>Variables</strong>
                  <p class="sx-help">Cliquer pour insérer au curseur.</p>
                  <div v-for="group in variableGroups" :key="group.label" class="dp-vars-group">
                    <span>{{ group.label }}</span>
                    <button v-for="v in group.items" :key="v" type="button" class="dp-chip" @click="insertVariable(v)">{{ v }}</button>
                  </div>
                </div>
              </div>
            </div>

            <div class="dp-preview">
              <div class="dp-preview-bar">
                <input v-model="previewCode" type="text" class="form-control form-control-sm" placeholder="Code colis (facultatif)" aria-label="Code colis pour l'aperçu" />
                <button type="button" class="btn btn-sm btn-outline-primary" :disabled="previewing" @click="runPreview">
                  <span v-if="previewing" class="spinner-border spinner-border-sm" role="status"></span>
                  <i v-else class="ph ph-eye" aria-hidden="true"></i> Aperçu
                </button>
                <span class="sx-help">Rien n'est envoyé au prestataire.</span>
              </div>
              <div v-if="preview" class="dp-preview-out">
                <small>{{ form.api_method }} {{ preview.url }} — colis {{ preview.coli_code }}</small>
                <pre>{{ pretty(preview.payload) }}</pre>
              </div>
            </div>
          </fieldset>

          <!-- Provider cities -->
          <fieldset class="dp-section">
            <legend>Villes du prestataire</legend>
            <p class="sx-help dp-auth-help">
              Saisissez l'ID de chaque ville dans la liste des villes du prestataire, puis utilisez la variable
              <code v-text="'{{coli.provider_city_id}}'"></code> dans le modèle (ex. <code v-text="'&quot;parcel-city&quot;: &quot;{{coli.provider_city_id}}&quot;'"></code>).
              Un colis dont la ville n'a pas d'ID n'est pas envoyé.
            </p>
            <div class="row g-2 mb-2">
              <div class="col-md-12 sx-field">
                <label for="dp-cities-endpoint">URL de la liste des villes du prestataire</label>
                <input id="dp-cities-endpoint" v-model="form.api_cities_endpoint" type="text" class="form-control form-control-sm" placeholder="https://api.prestataire.com/cities" />
              </div>
              <div class="col-md-4 sx-field">
                <label for="dp-cities-list">Chemin de la liste</label>
                <input id="dp-cities-list" v-model="form.api_cities_list_path" type="text" class="form-control form-control-sm" placeholder="data (vide = racine)" />
              </div>
              <div class="col-md-4 sx-field">
                <label for="dp-cities-id">Clé de l'ID</label>
                <input id="dp-cities-id" v-model="form.api_cities_id_path" type="text" class="form-control form-control-sm" placeholder="id" />
              </div>
              <div class="col-md-4 sx-field">
                <label for="dp-cities-name">Clé du nom</label>
                <input id="dp-cities-name" v-model="form.api_cities_name_path" type="text" class="form-control form-control-sm" placeholder="name" />
              </div>
              <div class="col-12 d-flex flex-wrap align-items-center gap-2">
                <button type="button" class="btn btn-sm btn-outline-primary" :disabled="importingCities" @click="importCities">
                  {{ importingCities ? "Import en cours…" : "Importer les villes (association par nom)" }}
                </button>
                <span v-if="importResult" class="sx-help">{{ importResult }}</span>
              </div>
            </div>
            <div class="dp-cities-bar">
              <input v-model="cityQuery" type="search" class="form-control form-control-sm" placeholder="Rechercher une ville" aria-label="Rechercher une ville" />
              <span class="sx-help">{{ mappedCount }} / {{ cities.length }} ville(s) renseignée(s)</span>
            </div>
            <div class="dp-cities">
              <div v-for="c in filteredCities" :key="c.id" class="dp-city">
                <label :for="'dp-city-' + c.id">{{ c.name }}</label>
                <input :id="'dp-city-' + c.id" v-model.trim="cityMap[c.id]" type="text" class="form-control form-control-sm" placeholder="ID prestataire" />
              </div>
              <p v-if="!filteredCities.length" class="sx-help">Aucune ville.</p>
            </div>
          </fieldset>

          <!-- Response -->
          <fieldset class="dp-section">
            <legend>Lecture de la réponse</legend>
            <div class="row g-3">
              <div class="col-md-6 sx-field">
                <label for="dp-tracking">Chemin du numéro de suivi</label>
                <input id="dp-tracking" v-model="form.api_tracking_path" type="text" class="form-control" placeholder="data.tracking_number" />
                <span class="sx-help">Notation pointée, ex. <code>data.0.code</code>.</span>
              </div>
              <div class="col-md-6 sx-field">
                <label for="dp-error">Chemin du message d'erreur</label>
                <input id="dp-error" v-model="form.api_error_path" type="text" class="form-control" placeholder="message" />
              </div>
              <div class="col-md-6 sx-field">
                <label for="dp-successpath">Chemin de l'indicateur de succès</label>
                <input id="dp-successpath" v-model="form.api_success_path" type="text" class="form-control" placeholder="success" />
                <span class="sx-help">Vide : tout code HTTP 2xx est un succès.</span>
              </div>
              <div class="col-md-6 sx-field">
                <label for="dp-successval">Valeur attendue</label>
                <input id="dp-successval" v-model="form.api_success_value" type="text" class="form-control" placeholder="true" :disabled="!form.api_success_path" />
                <span class="sx-help">Vide : la valeur doit simplement être vraie.</span>
              </div>
            </div>
          </fieldset>

          <!-- Advanced -->
          <fieldset class="dp-section">
            <legend>Paramètres spécifiques</legend>
            <div class="row g-3">
              <div class="col-md-6 sx-field">
                <label for="dp-headers">En-têtes supplémentaires (JSON)</label>
                <textarea id="dp-headers" v-model="form.api_headers" class="form-control dp-code" :class="{ 'is-invalid': errors.api_headers }" rows="4" spellcheck="false" placeholder='{"X-Account": "123"}'></textarea>
                <span v-if="errors.api_headers" class="sx-err">{{ errors.api_headers }}</span>
              </div>
              <div class="col-md-6 sx-field">
                <label for="dp-extra">Autres paramètres (JSON)</label>
                <textarea id="dp-extra" v-model="form.api_extra" class="form-control dp-code" :class="{ 'is-invalid': errors.api_extra }" rows="4" spellcheck="false" placeholder='{"store_id": 42, "service": "express"}'></textarea>
                <span v-if="errors.api_extra" class="sx-err">{{ errors.api_extra }}</span>
                <span v-else class="sx-help">Utilisables dans le modèle via <code v-text="'{{extra.store_id}}'"></code>.</span>
              </div>
            </div>
          </fieldset>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Annuler</button>
          <button type="submit" class="btn btn-primary" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm" role="status"></span>
            Enregistrer
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, reactive, ref } from "vue";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";
import { Modal } from "bootstrap";

/**
 * Create / edit modal of a delivery provider (prestataire) and its API integration.
 * Shared by the "Prestataires" settings page and the users pages, where a driver
 * (role_id 4) is established as provider: open(null, { id, name }) links the new provider to it.
 */
const authOptions = [
  { value: "none", label: "Aucune", help: "L'API ne demande pas d'authentification." },
  { value: "bearer", label: "Bearer token", help: "En-tête Authorization: Bearer <token>." },
  { value: "header", label: "Clé en en-tête", help: "La clé est envoyée dans un en-tête HTTP (ex. X-API-KEY)." },
  { value: "query", label: "Clé en paramètre", help: "La clé est ajoutée à l'URL (ex. ?api_key=…)." },
  { value: "basic", label: "Basic", help: "Identifiant et mot de passe (HTTP Basic)." },
  { value: "url", label: "Clé dans l'URL", help: "La clé est écrite dans l'URL de l'endpoint avec {{token}} (ex. /customers/{{extra.client_id}}/{{token}}/add-parcel)." },
  { value: "login", label: "Login → token", help: "Un appel d'authentification renvoie un token, utilisé ensuite en Bearer." },
];

const DEFAULT_TEMPLATE = `{
  "reference": "{{coli.code}}",
  "receiver_name": "{{coli.receiver}}",
  "receiver_phone": "{{coli.phone}}",
  "city": "{{coli.city_name}}",
  "address": "{{coli.address}}",
  "cod_amount": "{{coli.price}}",
  "product": "{{coli.product_nature}}",
  "comment": "{{coli.note}}",
  "fragile": "{{coli.fragile}}"
}`;

const variableGroups = [
  {
    label: "Colis",
    items: [
      "coli.code", "coli.receiver", "coli.phone", "coli.city_name", "coli.district", "coli.address", "coli.price",
      "coli.product_nature", "coli.note", "coli.fragile", "coli.can_open", "coli.customer_name", "coli.customer_phone", "coli.provider_city_id",
    ],
  },
  { label: "Bon d'envoi", items: ["note.note_ref", "note.sending_agency_name", "note.reception_agency_name", "note.parcels_num"] },
  { label: "Autres", items: ["transport.name", "transport.code_transport", "provider.code", "sacs", "today", "now", "extra.…"] },
];

const emptyForm = () => ({
  id: null as number | null,
  user_id: null as number | null,
  name: "",
  code: "",
  active: true,
  api_base_url: "",
  api_auth_type: "none",
  api_key_name: "",
  api_token: "",
  api_token_set: false,
  api_token_hint: "",
  clear_token: false,
  api_username: "",
  api_password: "",
  api_password_set: false,
  clear_password: false,
  api_auth_endpoint: "",
  api_auth_payload: "",
  api_auth_token_path: "",
  api_create_endpoint: "",
  api_method: "POST",
  api_body_format: "json",
  api_headers: "",
  api_payload_template: DEFAULT_TEMPLATE,
  api_tracking_path: "",
  api_success_path: "",
  api_success_value: "",
  api_error_path: "",
  api_timeout: 20 as number | null,
  api_extra: "",
  api_city_map: "",
  api_cities_endpoint: "",
  api_cities_list_path: "",
  api_cities_id_path: "",
  api_cities_name_path: "",
});

export default defineComponent({
  name: "DeliveryProviderFormModal",
  emits: ["saved"],
  setup(_, { emit, expose }) {
    const { showToast } = useAppToast();
    const form = reactive(emptyForm());
    const errors = reactive<Record<string, string>>({});
    const saving = ref(false);
    const modalEl = ref<HTMLElement | null>(null);
    const payloadEl = ref<HTMLTextAreaElement | null>(null);
    const driverName = ref("");
    let modal: Modal | null = null;

    // our city id -> provider's city id (stored as JSON in form.api_city_map)
    const cityMap = reactive<Record<string, string>>({});
    const cities = ref<{ id: number; name: string }[]>([]);
    const cityQuery = ref("");
    const filteredCities = computed(() => {
      const q = cityQuery.value.trim().toLowerCase();
      return q ? cities.value.filter((c) => c.name.toLowerCase().includes(q)) : cities.value;
    });
    const mappedCount = computed(() => cities.value.filter((c) => String(cityMap[c.id] ?? "").trim() !== "").length);
    const cityMapJson = () => {
      const out: Record<string, string> = {};
      Object.keys(cityMap).forEach((k) => {
        const v = String(cityMap[k] ?? "").trim();
        if (v) out[k] = v;
      });
      return Object.keys(out).length ? JSON.stringify(out) : "";
    };
    async function loadCities() {
      if (cities.value.length) return;
      try {
        const res = await api.get("/api/auth/citiesList");
        cities.value = (res.data || [])
          .map((c: any) => ({ id: c.id, name: String(c.name || "").trim() }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name, "fr"));
      } catch (e) {
        console.error("Error fetching cities:", e);
      }
    }

    // ── Import of the provider's city list, matched with ours by name (accents / case ignored) ──
    const importingCities = ref(false);
    const importResult = ref("");
    const normName = (s: string) =>
      s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9؀-ۿ]+/g, " ").trim();
    async function importCities() {
      if (!form.api_cities_endpoint.trim()) {
        showToast("Renseignez l'URL de la liste des villes.", "warning");
        return;
      }
      importingCities.value = true;
      importResult.value = "";
      try {
        await loadCities();
        const body: Record<string, any> = { ...form };
        ["api_token_set", "api_token_hint", "api_password_set"].forEach((k) => delete body[k]);
        const res = await api.post("/api/auth/deliveryProviders/cities", body);
        const byName = new Map<string, string>();
        (res.data?.cities || []).forEach((c: any) => {
          const key = normName(String(c.name));
          if (key && !byName.has(key)) byName.set(key, String(c.id));
        });
        let filled = 0;
        let missing = 0;
        cities.value.forEach((c) => {
          if (String(cityMap[c.id] ?? "").trim() !== "") return; // keeps what was typed by hand
          const id = byName.get(normName(c.name));
          if (id) {
            cityMap[c.id] = id;
            filled++;
          } else {
            missing++;
          }
        });
        importResult.value = `${res.data?.cities?.length || 0} ville(s) reçue(s) : ${filled} associée(s)${missing ? `, ${missing} sans correspondance (à saisir à la main)` : ""}.`;
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Import des villes impossible.", "error");
      } finally {
        importingCities.value = false;
      }
    }

    const authOf = (v: string) => authOptions.find((o) => o.value === v);
    const pretty = (v: unknown) => JSON.stringify(v, null, 2);

    const title = computed(() => {
      if (form.id) return `Modifier ${form.name}`;
      return form.user_id ? `Établir ${driverName.value} comme prestataire` : "Nouveau prestataire";
    });

    const payloadJsonError = computed(() => {
      if (!form.api_payload_template.trim()) return "";
      try {
        const v = JSON.parse(form.api_payload_template);
        return v && typeof v === "object" ? "" : "Le modèle doit être un objet JSON.";
      } catch (e: any) {
        return `JSON invalide : ${e.message}`;
      }
    });

    /** item: provider to edit (null = new); driver: driver account the new provider is linked to. */
    function open(item: Record<string, any> | null, driver: { id: number; name: string } | null = null) {
      Object.assign(form, emptyForm());
      driverName.value = driver?.name || item?.driver_name || "";
      if (item) {
        Object.keys(form).forEach((k) => {
          if (k in item && item[k] !== null && item[k] !== undefined) (form as any)[k] = item[k];
        });
        form.api_token = "";
        form.api_password = "";
      } else if (driver) {
        form.user_id = driver.id;
        form.name = driver.name;
      }
      Object.keys(cityMap).forEach((k) => delete cityMap[k]);
      try {
        Object.assign(cityMap, JSON.parse(form.api_city_map || "{}"));
      } catch (e) {
        /* invalid stored map: start empty */
      }
      cityQuery.value = "";
      loadCities();
      Object.keys(errors).forEach((k) => delete errors[k]);
      preview.value = null;
      previewCode.value = "";
      if (!modal && modalEl.value) modal = new Modal(modalEl.value);
      modal?.show();
    }

    async function save() {
      Object.keys(errors).forEach((k) => delete errors[k]);
      if (!form.name.trim()) errors.name = "Le nom du prestataire est obligatoire.";
      if (payloadJsonError.value) errors.api_payload_template = payloadJsonError.value;
      if (Object.keys(errors).length || saving.value) return;

      saving.value = true;
      try {
        const body: Record<string, any> = { ...form, api_city_map: cityMapJson() };
        ["id", "api_token_set", "api_token_hint", "api_password_set"].forEach((k) => delete body[k]);
        if (!body.user_id) delete body.user_id;
        const url = form.id ? `/api/auth/deliveryProviders/${form.id}` : "/api/auth/deliveryProviders";
        const res = await api.post(url, body);
        if (res.data?.success) {
          modal?.hide();
          showToast(form.id ? "Prestataire modifié." : form.user_id ? "Livreur établi comme prestataire." : "Prestataire ajouté.", "success");
          emit("saved", res.data.provider);
        } else {
          showToast(res.data?.message || "L'enregistrement a échoué.", "error");
        }
      } catch (error: any) {
        const errs = error?.response?.data?.errors || {};
        Object.keys(errs).forEach((k) => (errors[k] = errs[k][0]));
        showToast(error?.response?.data?.message || "Une erreur est survenue lors de l'enregistrement.", "error");
      } finally {
        saving.value = false;
      }
    }

    function insertVariable(v: string) {
      const el = payloadEl.value;
      const token = `{{${v === "extra.…" ? "extra.cle" : v}}}`;
      if (!el) {
        form.api_payload_template += token;
        return;
      }
      const start = el.selectionStart ?? form.api_payload_template.length;
      const end = el.selectionEnd ?? start;
      form.api_payload_template = form.api_payload_template.slice(0, start) + token + form.api_payload_template.slice(end);
      nextTick(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
    }

    // ── Preview (built server-side from the form, nothing is sent) ──
    const preview = ref<{ url: string; coli_code: string; payload: unknown } | null>(null);
    const previewCode = ref("");
    const previewing = ref(false);
    async function runPreview() {
      if (payloadJsonError.value) {
        showToast(payloadJsonError.value, "warning");
        return;
      }
      previewing.value = true;
      try {
        const res = await api.post("/api/auth/deliveryProviders/preview", {
          name: form.name,
          code: form.code,
          api_base_url: form.api_base_url,
          api_create_endpoint: form.api_create_endpoint,
          api_payload_template: form.api_payload_template,
          api_extra: form.api_extra,
          api_city_map: cityMapJson(),
          coli_code: previewCode.value.trim() || null,
        });
        preview.value = res.data;
      } catch (error: any) {
        preview.value = null;
        showToast(error?.response?.data?.message || "Aperçu impossible.", "error");
      } finally {
        previewing.value = false;
      }
    }

    onBeforeUnmount(() => modal?.dispose());
    expose({ open });

    return {
      authOptions,
      authOf,
      pretty,
      title,
      driverName,
      form,
      errors,
      saving,
      modalEl,
      payloadEl,
      payloadJsonError,
      open,
      save,
      variableGroups,
      cityMap,
      cities,
      cityQuery,
      filteredCities,
      mappedCount,
      importCities,
      importingCities,
      importResult,
      insertVariable,
      preview,
      previewCode,
      previewing,
      runPreview,
    };
  },
});
</script>

<style scoped>
.dp-section {
  margin: 0 0 22px;
  padding: 16px 18px 18px;
  border: 1px solid var(--ui-border);
  border-radius: 12px;
}
.dp-section legend {
  float: none;
  width: auto;
  margin: 0;
  padding: 0 8px;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.dp-switch {
  padding-top: 8px;
}
.dp-seg {
  flex-wrap: wrap;
}
.dp-auth-help {
  display: block;
  margin: 8px 0 14px;
}
.dp-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  line-height: 1.5;
}
.dp-vars {
  height: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--ui-accent-soft);
}
.dp-vars p {
  margin: 2px 0 8px;
}
.dp-vars-group {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 10px;
}
.dp-vars-group > span {
  width: 100%;
  color: var(--ui-text-secondary);
  font-size: 12px;
  font-weight: var(--ui-fw-bold);
}
.dp-chip {
  padding: 2px 8px;
  border: 1px solid var(--ui-border);
  border-radius: 999px;
  background: var(--ui-surface);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
}
.dp-chip:hover {
  border-color: var(--ui-accent);
  color: var(--ui-accent-text);
}
.dp-cities-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.dp-cities-bar .form-control {
  max-width: 260px;
}
.dp-cities {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 8px 14px;
  max-height: 320px;
  overflow: auto;
}
.dp-city {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.dp-city label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--ui-fs-sm);
}
.dp-city .form-control {
  width: 110px;
}
.dp-preview {
  margin-top: 14px;
}
.dp-preview-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.dp-preview-bar .form-control {
  max-width: 220px;
}
.dp-preview-out {
  margin-top: 10px;
}
.dp-preview-out pre {
  max-height: 280px;
  margin: 4px 0 0;
  padding: 12px;
  overflow: auto;
  border-radius: 8px;
  background: var(--ui-surface-2, #f4f5f7);
  font-size: 12.5px;
}
.dp-driver {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: 0 0 18px;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--ui-accent-soft);
}
.dp-driver > i {
  font-size: 22px;
  color: var(--ui-accent-text);
}
.dp-driver span {
  display: block;
  margin-top: 2px;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
}
</style>
